import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import ResponsiveModal from 'presentation/shared/components/ResponsiveModal'
import ProvidersTable from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderTables/ProvidersTable'
import ProvidersList from 'presentation/doctor/components/SurgicalOrder/SurgicalOrderLists/ProvidersList'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import TextAreaNew from 'presentation/shared/components/TextAreaNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import { ProviderV2Props } from 'presentation/contexts/surgical-order-v2/types'
import { ScrollableTableContainer } from '../components/styles'
import { DataSource } from 'common/enum/data-source'
import { v4 as uuidv4 } from 'uuid'

import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'

type AddProviderResponsiveModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (providers: ProviderV2Props[]) => void
  providers: ProviderV2Props[]
  allProviders: ProviderV2Props[]
}

const AddProviderResponsiveModal = ({
  isOpen,
  onClose,
  onConfirm,
  providers,
  allProviders
}: AddProviderResponsiveModalProps) => {
  const [providerName, setProviderName] = useState('')
  const [justification, setJustification] = useState('')
  const [addedProviders, setAddedProviders] =
    useState<ProviderV2Props[]>(providers)

  useEffect(() => {
    if (isOpen) {
      setAddedProviders(providers)
    }
  }, [isOpen])

  const resetModalState = () => {
    setProviderName('')
    setJustification('')
  }

  const handleClose = () => {
    resetModalState()
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(addedProviders)
    resetModalState()
  }

  const handleAddProvider = () => {
    if (!providerName.trim() || !justification.trim()) {
      toast.error('Preencha o nome do fornecedor e a justificativa')
      return
    }

    const providerExistsInExternalTable =
      allProviders.some(
        (p) => p.name.toLowerCase().trim() === providerName.toLowerCase().trim()
      ) ||
      addedProviders.some(
        (p) => p.name.toLowerCase().trim() === providerName.toLowerCase().trim()
      )

    if (providerExistsInExternalTable) {
      toast.error('Este fornecedor já está na listagem')
      return
    }

    const newProvider: ProviderV2Props = {
      code: uuidv4(),
      name: providerName.trim(),
      reason: justification.trim(),
      source: DataSource.DOCTOR,
      is_selected: true
    }

    setAddedProviders((prev) => [...prev, newProvider])
    setProviderName('')
    setJustification('')
  }

  const handleDeleteProvider = (providerCode: string) => {
    setAddedProviders((prev) => prev.filter((p) => p.code !== providerCode))
    toast.success('Fornecedor removido com sucesso')
  }

  const isAddButtonDisabled = !providerName.trim() || !justification.trim()
  const isConfirmButtonDisabled = addedProviders.length === 0

  return (
    <ResponsiveModal
      isOpen={isOpen}
      title="Adicionar fornecedores"
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmButtonLabel="Salvar e prosseguir"
      disableConfirmButton={isConfirmButtonDisabled}
    >
      {(isMobile) => (
        <>
          <S.FormContainer>
            <TextFieldNew
              name="providerName"
              label="Nome do fornecedor"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelColor="textSecondary"
              labelOverlapping
              placeholder="Digite o nome do fornecedor"
              value={providerName}
              onChange={(e) => setProviderName(e.target.value)}
              required
              requiredColor="textSecondary"
            />

            <TextAreaNew
              name="justification"
              label="Justificativa"
              labelSize="xxsmall"
              labelWeight="semiBold"
              labelColor="textSecondary"
              labelOverlapping
              placeholder="Justifique a indicação de fornecedor divergente do sugerido"
              value={justification}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setJustification(e.target.value)
              }
              rows={4}
              maxLength={500}
              required
              requiredColor="textSecondary"
            />
          </S.FormContainer>

          <ButtonNew
            outlined
            size="large"
            fullWidth
            onClick={handleAddProvider}
            disabled={isAddButtonDisabled}
          >
            Indicar fornecedor
          </ButtonNew>

          {!!addedProviders && addedProviders.length > 0 && (
            <>
              <HeadingNew
                as="h2"
                color="textSecondary"
                weight="bold"
                size="xsmall"
                style={{
                  textAlign: 'start',
                  marginTop: '2rem',
                  marginBottom: '0.5rem'
                }}
              >
                Fornecedores adicionados:
              </HeadingNew>

              <ScrollableTableContainer>
                {isMobile ? (
                  <ProvidersList
                    initialRows={addedProviders}
                    onDelete={handleDeleteProvider}
                  />
                ) : (
                  <ProvidersTable
                    initialRows={addedProviders}
                    onDelete={handleDeleteProvider}
                  />
                )}
              </ScrollableTableContainer>
            </>
          )}
        </>
      )}
    </ResponsiveModal>
  )
}

export default AddProviderResponsiveModal
